"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import gsap from "gsap";
import {
  FaInstagram,
  FaFacebookF,
  FaTiktok,
  FaYoutube,
} from "react-icons/fa";
import { X, Send } from "lucide-react";

const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/books", label: "Books" },
  { href: "/contact", label: "Contact" },
];

const SOCIALS = [
  {
    icon: FaInstagram,
    label: "Instagram",
    href: "https://www.instagram.com/burakozgurmd",
    color: "#E1306C",
  },
  {
    icon: FaYoutube,
    label: "YouTube",
    href: "https://www.youtube.com/@burakozgurmd",
    color: "#FF0000",
  },
  {
    icon: FaTiktok,
    label: "TikTok",
    href: "https://www.tiktok.com/@burak.ozgur.md",
    color: "#fff",
  },
  {
    icon: FaFacebookF,
    label: "Facebook",
    href: "https://www.facebook.com/BurakOzgurMD",
    color: "#1877F2",
  },
];

export default function FloatingNav() {
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const isAnimating = useRef(false);
  const overlayRef = useRef(null);
  const tlRef = useRef(null);

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    const lineWraps = overlay.querySelectorAll(
      ".bo-nav-socials .bo-line-wrap, .bo-nav-primary-links .bo-line-wrap, .bo-nav-secondary-links .bo-line-wrap"
    );
    const navBgs = overlay.querySelectorAll(".bo-nav-bg");
    const navItems = overlay.querySelector(".bo-nav-items");

    const tl = gsap.timeline({
      paused: true,
      defaults: { ease: "power3.inOut" },
      onReverseComplete: () => {
        gsap.set(lineWraps, { y: 22, autoAlpha: 0 });
        isAnimating.current = false;
      },
      onComplete: () => { isAnimating.current = false; },
    });

    tl.to(navBgs, { scaleY: 1, duration: 0.75, stagger: 0.1 });
    tl.to(
      navItems,
      { clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)", duration: 0.75 },
      "-=0.6"
    );
    tl.fromTo(
      lineWraps,
      { y: 22, autoAlpha: 0 },
      { y: 0, autoAlpha: 1, duration: 0.75, stagger: 0.05, ease: "power3.out" },
      0.85
    );

    tlRef.current = tl;
    return () => {
      tl.kill();
      gsap.set(lineWraps, { clearProps: "all" });
    };
  }, []);

  useEffect(() => {
    setContactOpen(false);
    if (!menuOpen) return;
    setMenuOpen(false);
    const tl = tlRef.current;
    if (tl) { isAnimating.current = true; tl.reverse(); }
  }, [pathname]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setContactOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Bridge: ContextualDock dispatches this event when the "+" is pressed in
  // sections configured with action: "openContact" (e.g. the Reach section).
  useEffect(() => {
    const onOpen = () => setContactOpen(true);
    window.addEventListener("bo:open-contact", onOpen);
    return () => window.removeEventListener("bo:open-contact", onOpen);
  }, []);

  // Full-screen menu overlay is now owned by <JamMenu/>. FloatingNav is
  // kept for the contact popup only — it no longer listens for
  // bo:open-menu. Its legacy overlay markup stays mounted but hidden
  // because nothing calls its timeline anymore.

  const toggleMenu = () => {
    if (isAnimating.current || !tlRef.current) return;
    isAnimating.current = true;
    setContactOpen(false);
    setMenuOpen((prev) => {
      const next = !prev;
      if (next) tlRef.current.play(0);
      else tlRef.current.reverse();
      return next;
    });
  };

  const closeMenu = () => {
    if (!menuOpen || isAnimating.current || !tlRef.current) return;
    isAnimating.current = true;
    setMenuOpen(false);
    tlRef.current.reverse();
  };

  const toggleContact = () => setContactOpen((v) => !v);

  return (
    <>
      <div ref={overlayRef} className={`bo-nav-overlay${menuOpen ? " is-open" : ""}`}>
        <div className="bo-nav-bg" />
        <div className="bo-nav-bg" />
        <div className="bo-nav-bg" />
        <div className="bo-nav-bg" />
        <div className="bo-nav-items">
          <div className="bo-nav-items-col">
            <div className="bo-nav-socials">
              <a href="https://www.instagram.com/burakozgurmd" target="_blank" rel="noreferrer">
                <span className="bo-line-mask"><span className="bo-line-wrap">Instagram</span></span>
              </a>
              <a href="https://www.youtube.com/@burakozgurmd" target="_blank" rel="noreferrer">
                <span className="bo-line-mask"><span className="bo-line-wrap">YouTube</span></span>
              </a>
              <a href="https://www.tiktok.com/@burak.ozgur.md" target="_blank" rel="noreferrer">
                <span className="bo-line-mask"><span className="bo-line-wrap">TikTok</span></span>
              </a>
            </div>
          </div>
          <div className="bo-nav-items-col">
            <nav className="bo-nav-primary-links">
              {NAV_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={closeMenu}>
                  <span className="bo-line-mask"><span className="bo-line-wrap">{l.label}</span></span>
                </a>
              ))}
            </nav>
            <div className="bo-nav-secondary-links">
              <a href="/about" onClick={closeMenu}>
                <span className="bo-line-mask"><span className="bo-line-wrap">Biography</span></span>
              </a>
              <a href="/contact" onClick={closeMenu}>
                <span className="bo-line-mask"><span className="bo-line-wrap">Book a Session</span></span>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`bo-contact-backdrop${contactOpen ? " is-open" : ""}`}
        onClick={() => setContactOpen(false)}
        aria-hidden="true"
      />

      <div
        className={`bo-contact-popup${contactOpen ? " is-open" : ""}`}
        role="dialog"
        aria-label="Contact Burak Ozgur"
        aria-modal="true"
      >
        <button
          className="bo-cp-close"
          onClick={() => setContactOpen(false)}
          aria-label="Close"
          type="button"
        >
          <X size={16} />
        </button>

        <div className="bo-cp-header">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            className="bo-cp-avatar"
            src="https://for-media.b-cdn.net/ozgurmd/burak-ozgur-headshot-2.JPG"
            alt="Burak M. Ozgur, MD"
          />
          <div className="bo-cp-id">
            <div className="bo-cp-name">Burak M. Ozgur, MD</div>
            <div className="bo-cp-title">Director, Hoag Spine Institute</div>
            <div className="bo-cp-location">Newport Beach, CA</div>
          </div>
        </div>

        <div className="bo-cp-divider" />

        <div className="bo-cp-socials-wrap">
          <span className="bo-cp-section-label">Subscribe to witness the pursuit of the impossible.</span>
          <div className="bo-cp-socials">
            {SOCIALS.map(({ icon: Icon, label, href, color }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="bo-cp-social-btn"
                aria-label={label}
              >
                <Icon size={18} style={{ color }} />
                <span>{label}</span>
              </a>
            ))}
          </div>
        </div>

        <div className="bo-cp-divider" />

        <div className="bo-cp-form">
          <span className="bo-cp-section-label">Reach out</span>
          <a
            className="bo-cp-submit"
            href="mailto:contact@ozgurmd.com"
          >
            <span>Email contact@ozgurmd.com</span>
            <Send size={14} />
          </a>
        </div>
      </div>

      <div className="bo-float-pill">
        <button
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          className={`bo-float-hamburger${menuOpen ? " open" : ""}`}
          onClick={toggleMenu}
          type="button"
        >
          <span />
          <span />
        </button>
        <span className="bo-float-divider" />
        <button
          className="bo-float-book-label"
          onClick={toggleContact}
          type="button"
          aria-expanded={contactOpen}
        >
          Book Burak Ozgur
        </button>
        <button
          className="bo-float-cta"
          onClick={toggleContact}
          type="button"
          aria-expanded={contactOpen}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="https://for-media.b-cdn.net/ozgurmd/burak-ozgur-headshot-2.JPG"
            alt=""
            aria-hidden="true"
          />
          <span>Book Now</span>
        </button>
      </div>
    </>
  );
}
